import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { waGeneral } from "@/lib/whatsapp";

// Line-by-line masked reveal
const line = {
    hidden: { y: "110%" },
    show: (i = 0) => ({
        y: 0,
        transition: { delay: 0.2 + i * 0.12, duration: 1.1, ease: [0.77, 0, 0.175, 1] },
    }),
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
    const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section
            ref={ref}
            id="hero"
            className="relative min-h-[100svh] pt-24 md:pt-28 pb-16 overflow-hidden bg-bone grain"
            data-testid="hero-section"
        >
            {/* floating brand orbs */}
            <motion.div
                style={{ y: y2 }}
                className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl"
                aria-hidden
            >
                <div className="w-full h-full bg-brand-red/60" />
            </motion.div>
            <motion.div
                style={{ y }}
                className="absolute top-1/3 -left-32 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl"
                aria-hidden
            >
                <div className="w-full h-full bg-brand-blue/50" />
            </motion.div>

            <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
                {/* top eyebrow row */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.05, duration: 0.6 }}
                    className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-ink/60"
                >
                    <span>Est. Muscat · Rusayl Industrial Estate</span>
                    <span className="hidden md:inline">N 23.5859° · E 58.4059°</span>
                    <span>Made in Oman</span>
                </motion.div>

                <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6 items-end">
                    <div className="col-span-12 md:col-span-8">
                        <h1
                            className="font-display font-medium leading-[0.92] tracking-[-0.02em] text-ink"
                            data-testid="hero-heading"
                        >
                            <span className="block overflow-hidden">
                                <motion.span
                                    variants={line}
                                    custom={0}
                                    initial="hidden"
                                    animate="show"
                                    className="block text-[14vw] md:text-[10.5vw] lg:text-[9.5vw]"
                                >
                                    Colour, cast
                                </motion.span>
                            </span>
                            <span className="block overflow-hidden -mt-2">
                                <motion.span
                                    variants={line}
                                    custom={1}
                                    initial="hidden"
                                    animate="show"
                                    className="block text-[14vw] md:text-[10.5vw] lg:text-[9.5vw]"
                                >
                                    from the{" "}
                                    <span className="italic font-normal text-brand-red">desert.</span>
                                </motion.span>
                            </span>
                        </h1>
                    </div>

                    <div className="col-span-12 md:col-span-4 md:pb-3">
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.8 }}
                            className="text-ink/70 text-base md:text-lg max-w-sm"
                        >
                            Mist Paints is a Muscat-made coatings house — engineered for Oman's sun,
                            salt and stone. Premium finishes with the patience of a tortoise.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.8 }}
                            className="mt-6 flex flex-wrap gap-3"
                        >
                            <a
                                href="/products"
                                className="btn-pill bg-ink text-bone hover:bg-brand-red"
                                data-testid="hero-explore-cta"
                            >
                                Explore products
                                <ArrowDown size={16} className="-rotate-45" />
                            </a>
                            <a
                                href={waGeneral()}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-pill border border-ink/25 hover:bg-ink hover:text-bone"
                                data-testid="hero-whatsapp-cta"
                            >
                                Order via WhatsApp
                            </a>
                        </motion.div>
                    </div>
                </div>

                {/* Bottom marker row */}
                <motion.div
                    style={{ opacity }}
                    className="mt-20 md:mt-28 flex items-end justify-between border-t border-ink/15 pt-6"
                >
                    <div className="flex gap-8 md:gap-14 text-sm">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                                Chapter
                            </div>
                            <div className="font-display text-3xl md:text-4xl">00 / Overture</div>
                        </div>
                        <div className="hidden md:block">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                                Palette
                            </div>
                            <div className="flex gap-1.5 mt-1">
                                {["#ED2938", "#EF6424", "#EEC12B", "#33672E", "#214D8D"].map((c) => (
                                    <span
                                        key={c}
                                        className="block w-6 h-6 rounded-full ring-1 ring-ink/10"
                                        style={{ background: c }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <a
                        href="#manifesto"
                        className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ink/60 hover:text-ink"
                    >
                        Scroll <ArrowDown size={14} className="animate-bounce" />
                    </a>
                </motion.div>
            </div>
        </section>
    );
};
